import { useQuery } from '@tanstack/react-query';
import {
  BellOff,
  CircleCheck,
  CircleX,
  Ellipsis,
  Eye,
  History,
  Mail,
  MailWarning,
  Webhook,
  Wrench,
  type LucideIcon,
} from 'lucide-react';
import { Fragment } from 'react';
import { useParams } from 'react-router-dom';
import { Panel, Pill, SectionHeader, Skeleton, StatusGlyph, StatusPill } from '@/components/surface/ui';
import { toneWash, type SurfaceTone } from '@/components/surface/tones';
import { getErrorMessage } from '@/lib/errors';
import { incidentKey, monitoringApi } from '@/lib/monitoring-api';
import { cn } from '@/lib/utils';
import { PageHeader } from '@/modules/automation/ui';
import type { IncidentDetail, TimelineEvent, TimelineMessage } from '@/types/api';

/**
 * One outage, told in order: the checks that confirmed it, the alert, any fix, and what ended it.
 *
 * ## Why it exists
 *
 * An alert says "Confirmed by: 4 failed checks in a row". The owner read one the next morning and
 * could not find the four checks: the strip on the Monitoring page holds the last 40, about three
 * hours of a five-minute monitor, and by then they had scrolled away (2026-10-28). An outage now keeps
 * its own checks, and this page shows them beside the messages they caused.
 *
 * ## Times are the reader's, and the page says so
 *
 * Alert emails print UTC; this page prints the browser's time. Reading "08:40 UTC" in an email and
 * looking for 08:40 on a page set five hours ahead finds nothing, so the zone is written out.
 */
export function OutagePage() {
  const { id = '' } = useParams<{ id: string }>();

  const detail = useQuery({
    queryKey: incidentKey(id),
    queryFn: () => monitoringApi.incident(id),
    enabled: id !== '',
  });

  if (detail.isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-24" />
        <Skeleton className="h-28" />
        <Skeleton className="h-[28rem]" />
      </div>
    );
  }

  if (detail.isError || detail.data === undefined) {
    return (
      <div className="space-y-4">
        <PageHeader title="Outage Not Found" back={{ to: '/monitoring', label: 'Monitoring' }} />
        <p className="text-sm text-muted-foreground">
          {detail.isError ? getErrorMessage(detail.error) : 'This outage does not exist.'}
        </p>
      </div>
    );
  }

  return <Outage data={detail.data} />;
}

function Outage({ data }: { data: IncidentDetail }) {
  const { incident, monitor, timeline } = data;
  const open = incident.is_open;
  const confirmations = monitor?.confirmations ?? 4;
  const hasChecks = timeline.some((event) => event.type === 'check');

  return (
    <div className="space-y-6">
      <PageHeader
        back={{ to: '/monitoring', label: 'Monitoring' }}
        eyebrow={
          <>
            <StatusPill tone={open ? 'danger' : 'success'} label={open ? 'Down now' : 'Back up'} pulse={open} />
            <Pill tone="muted">Outage</Pill>
            {incident.remediation !== null && (
              <Pill tone={incident.remediation.status === 'fixed' ? 'success' : 'muted'} icon={Wrench}>
                {incident.remediation.status_label}
              </Pill>
            )}
          </>
        }
        title={monitor?.name ?? (incident.monitor_name !== '' ? incident.monitor_name : 'Outage')}
        description={monitor !== null ? <span className="font-mono text-[13px]">{monitor.target}</span> : undefined}
      />

      <Panel label="Summary">
        <dl className="grid gap-x-6 gap-y-4 px-5 py-4 sm:grid-cols-2 xl:grid-cols-4">
          <Figure label="Down from" value={dayAndTime(incident.started_at)} hint="When the first check failed." />
          <Figure
            label="Back up at"
            value={incident.resolved_at === null ? 'Still down' : time(incident.resolved_at)}
            hint={
              incident.resolved_at === null
                ? `Checked again every ${spoken(monitor?.interval_seconds ?? 300)} while it is down.`
                : 'When a check passed again.'
            }
          />
          <Figure
            label="Down for"
            value={
              incident.duration_seconds === null
                ? `${spoken(secondsSince(incident.started_at))} so far`
                : spoken(incident.duration_seconds)
            }
            hint="From the first failed check to the one that passed."
          />
          <Figure
            label="Alert"
            value={alertSummary(data)}
            hint={`An alert goes out after ${confirmations} failed checks in a row.`}
          />
        </dl>

        <p className={cn('mx-5 mb-5 rounded-lg px-4 py-2.5 text-sm', toneWash.danger)}>
          <span className="font-medium">Reason: </span>
          {incident.cause}
        </p>
      </Panel>

      <Panel>
        <SectionHeader
          icon={History}
          title="What Happened"
          description={`Every check, fix and message, in order. ${recheckSentence(data)} Times are in your time zone (${zoneOf(incident.started_at)}); alert emails show UTC.`}
        />

        {!hasChecks && (
          <p className="px-5 pb-3 text-sm text-muted-foreground">
            The checks for this outage are no longer kept. It is older than the {data.retention_days} days
            of check history, and it happened before outages kept their own checks.
          </p>
        )}

        <ol className="pb-2">
          {timeline.map((event, index) => {
            const previous = index > 0 ? timeline[index - 1] : undefined;
            const newDay = previous === undefined || day(previous.at) !== day(event.at);
            const row = describe(event);

            return (
              <Fragment key={`${event.type}-${event.at}-${index}`}>
                {newDay && (
                  <li className="px-5 pb-1 pt-3 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    {day(event.at)}
                  </li>
                )}
                <li className="flex items-start gap-3 px-5 py-2">
                  <span className="w-[5.75rem] shrink-0 pt-1 text-[13px] tabular-nums text-muted-foreground">
                    {time(event.at)}
                  </span>
                  <StatusGlyph icon={row.icon} tone={row.tone} label={row.title} size="sm" />
                  <div className="min-w-0 flex-1 pt-0.5">
                    <p className="text-sm font-medium">{row.title}</p>
                    {row.detail !== undefined && row.detail !== '' && (
                      <p className="break-words text-[13px] text-muted-foreground">{row.detail}</p>
                    )}
                  </div>
                  {row.aside !== undefined && (
                    <span className="shrink-0 pt-1 text-[13px] tabular-nums text-muted-foreground">{row.aside}</span>
                  )}
                </li>
              </Fragment>
            );
          })}
        </ol>
      </Panel>
    </div>
  );
}

function Figure({ label, value, hint }: { label: string; value: string; hint: string }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="text-lg font-semibold">{value}</dd>
      <dd className="text-xs text-muted-foreground">{hint}</dd>
    </div>
  );
}

interface Row {
  icon: LucideIcon;
  tone: SurfaceTone;
  title: string;
  detail?: string;
  aside?: string;
}

/** What one entry says. The platform decided what each entry is; this only words it. */
function describe(event: TimelineEvent): Row {
  switch (event.type) {
    case 'check': {
      const answer = event.status_code !== null ? `HTTP ${event.status_code} in ${took(event.latency_ms)}.` : `Answered in ${took(event.latency_ms)}.`;

      if (event.phase === 'before') {
        return event.healthy
          ? { icon: CircleCheck, tone: 'success', title: 'Check passed', detail: `${answer} The last check before the outage.` }
          : { icon: CircleX, tone: 'danger', title: 'Check failed', detail: event.detail, aside: took(event.latency_ms) };
      }

      if (event.phase === 'recovery') {
        return { icon: CircleCheck, tone: 'success', title: 'Check passed: back up', detail: answer };
      }

      if (event.phase === 'confirming' && event.step !== null && event.of !== null) {
        return {
          icon: CircleX,
          tone: 'danger',
          title: event.step === event.of ? `Check ${event.step} of ${event.of} failed: marked down` : `Check ${event.step} of ${event.of} failed`,
          detail: event.detail,
          aside: took(event.latency_ms),
        };
      }

      return { icon: CircleX, tone: 'danger', title: 'Check failed, still down', detail: event.detail, aside: took(event.latency_ms) };
    }

    case 'message':
      return message(event);

    case 'fix_started':
      return { icon: Wrench, tone: 'muted', title: 'Trying a fix', detail: 'The alert waits while it runs.' };

    case 'fix':
      return {
        icon: Wrench,
        tone: event.succeeded ? 'success' : 'warning',
        title: event.label !== '' ? event.label : event.action === 'none' ? 'Nothing was tried' : 'Automatic fix',
        detail: event.detail,
      };

    case 'fix_finished':
      return {
        icon: Wrench,
        tone: event.status === 'fixed' ? 'success' : event.status === 'failed' ? 'danger' : 'muted',
        title: event.status_label,
      };

    case 'omitted':
      return {
        icon: Ellipsis,
        tone: 'muted',
        title: `${event.count} more failed checks`,
        detail: `Until ${time(event.until)}. Not listed, to keep the page short.`,
      };

    case 'acknowledged':
      return { icon: Eye, tone: 'muted', title: 'Marked as seen' };

    case 'summary':
      return {
        icon: Mail,
        tone: 'muted',
        title: 'Listed in the summary of automatic fixes',
        detail: 'Sent where your alerts go, at most once an hour.',
      };
  }
}

function message(event: TimelineMessage): Row {
  const down = event.event === 'monitor.down';
  const what = down ? 'Alert' : 'Back up message';

  if (!event.recorded) {
    return {
      icon: Mail,
      tone: 'primary',
      title: down ? 'Alert sent' : 'Back up message sent',
      detail: 'Where it went was not recorded: outages before 28 October 2026 kept only that one was sent.',
    };
  }

  if (event.channel === '') {
    return { icon: BellOff, tone: 'warning', title: down ? 'No alert sent' : 'No back up message sent', detail: event.error };
  }

  const where = event.channel === 'webhook' ? `the webhook at ${event.to}` : event.to;

  if (!event.sent) {
    return { icon: MailWarning, tone: 'danger', title: `${what} to ${where} could not be sent`, detail: event.error };
  }

  if (event.channel === 'email') {
    return { icon: Mail, tone: 'primary', title: down ? `Alert email sent to ${event.to}` : `Back up email sent to ${event.to}` };
  }

  return { icon: event.channel === 'webhook' ? Webhook : Mail, tone: 'primary', title: `${what} sent to ${where}` };
}

/** The alert in one phrase, for the summary: whether anybody was told, and when. */
function alertSummary({ incident, timeline }: IncidentDetail): string {
  const alerts = timeline.filter(
    (event): event is TimelineMessage => event.type === 'message' && event.event === 'monitor.down',
  );
  const sent = alerts.find((alert) => alert.sent);

  if (sent !== undefined) {
    const addresses = alerts.filter((alert) => alert.sent && alert.to !== '').length;

    return addresses > 1 ? `Sent at ${time(sent.at)} to ${addresses} addresses` : `Sent at ${time(sent.at)}`;
  }

  if (alerts.some((alert) => alert.recorded && alert.channel === '')) {
    return 'Not sent: nobody is set up';
  }

  if (alerts.length > 0) {
    return 'Could not be sent';
  }

  if (incident.remediation?.status === 'fixed') {
    return 'Not needed: fixed automatically';
  }

  if (incident.is_open && incident.remediation?.status === 'pending') {
    return 'Waiting while a fix runs';
  }

  return 'Not sent';
}

function recheckSentence({ monitor }: IncidentDetail): string {
  if (monitor === null) {
    return '';
  }

  return `A failed check is tried again ${spoken(monitor.recheck_seconds)} after it ends, and ${monitor.confirmations} failures in a row mark it down.`;
}

// ── Formatting ───────────────────────────────────────────────────────────────────────────────

const timeFormat = new Intl.DateTimeFormat(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' });
const dayFormat = new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'short', year: 'numeric' });

function time(iso: string): string {
  return timeFormat.format(new Date(iso));
}

function day(iso: string): string {
  return dayFormat.format(new Date(iso));
}

function dayAndTime(iso: string): string {
  return `${day(iso)}, ${time(iso)}`;
}

/** "UTC+05:00", for the moment given: a zone with summer time has two. */
function zoneOf(iso: string): string {
  const minutes = -new Date(iso).getTimezoneOffset();

  if (minutes === 0) {
    return 'UTC';
  }

  const sign = minutes > 0 ? '+' : '-';
  const hours = String(Math.floor(Math.abs(minutes) / 60)).padStart(2, '0');
  const rest = String(Math.abs(minutes) % 60).padStart(2, '0');

  return `UTC${sign}${hours}:${rest}`;
}

function secondsSince(iso: string): number {
  return Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
}

/** As the alert emails say it, so "Down for: 8 minutes" in an email reads the same here. */
function spoken(seconds: number): string {
  if (seconds < 60) {
    return `${seconds} second${seconds === 1 ? '' : 's'}`;
  }

  if (seconds < 3600) {
    const minutes = Math.round(seconds / 60);

    return `${minutes} minute${minutes === 1 ? '' : 's'}`;
  }

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.round((seconds % 3600) / 60);
  const hourText = `${hours} hour${hours === 1 ? '' : 's'}`;

  return minutes === 0 ? hourText : `${hourText} ${minutes} minute${minutes === 1 ? '' : 's'}`;
}

/** How long a check took, to the tenth of a second once it is more than one. */
function took(ms: number): string {
  return ms < 1000 ? `${ms} ms` : `${(ms / 1000).toFixed(1)} s`;
}
